import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, ArrowRight, Clock, Trophy, Target, ShieldAlert } from "lucide-react";
import client from "../lib/api";
import { SignalCard } from "../components/hub/SignalFeed";
import { JoinPremiumBox } from "../components/hub/JoinPremiumBox";

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const KIND = { engine: "Free Signal", scalp: "Premium Scalp", vip: "VIP Signal" };

function Level({ label, value, tone, locked, i }) {
  return (
    <div className="surface px-4 py-3 animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${i * 70}ms`, animationFillMode: "both" }}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      {locked ? (
        <div className="flex items-center gap-2 mt-1 text-slate-500"><Lock size={14} className="text-gold" /><span className="font-mono blur-[5px] select-none">0000.00</span></div>
      ) : (
        <div className={`font-mono text-lg mt-0.5 ${tone || "text-white"}`}>{value}</div>
      )}
    </div>
  );
}

function Levels({ s }) {
  const entry = s.entry_low && s.entry_high ? `${fmt(s.entry_low)} – ${fmt(s.entry_high)}` : fmt(s.entry);
  const rows = [["Entry", entry], ["Stop Loss", fmt(s.sl), "text-bear"], ["TP 1", fmt(s.tp1), "text-bull"], ["TP 2", fmt(s.tp2), "text-bull"], ["TP 3", fmt(s.tp3), "text-bull"]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="signal-levels">
      {rows.map(([l, v, t], i) => <Level key={l} i={i} label={l} value={v} tone={t} locked={s.locked} />)}
      {!s.locked && s.rr && <Level i={5} label="Risk : Reward" value={`1 : ${s.rr}`} tone="text-gold" />}
    </div>
  );
}

export default function SignalAlert() {
  const { src, sid } = useParams();
  return src === "feed" ? <FeedAlert sid={sid} /> : <LegacySignal src={src} sid={sid} />;
}

function FeedAlert({ sid }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    client.get(`/public/feed/${sid}`).then(({ data }) => setS(data)).catch(() => setErr(true));
  }, [sid]);
  if (err) return <div className="min-h-screen grid place-items-center text-white" style={{ background: "var(--app-bg)" }} data-testid="signal-not-found">Signal nahi mila</div>;
  if (!s) return <div className="min-h-screen" style={{ background: "var(--app-bg)" }} />;
  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }} data-testid="signal-alert-page">
      <header className="max-w-2xl mx-auto px-5 pt-6 flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center bg-gold text-black font-head font-bold text-sm">F</div>
        <span className="font-head font-bold text-white">Fateh FX</span>
        <Link to="/login" className="ml-auto text-xs text-slate-400 hover:text-white">Login</Link>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-gold">Fateh FX {s.kind === "signal" ? "Signal" : "Update"}</div>
        <h1 className="font-head font-bold text-white text-4xl sm:text-5xl mt-3" data-testid="signal-title">{s.instrument} <span className={s.direction === "BUY" ? "text-bull" : s.direction === "SELL" ? "text-bear" : "text-gold"}>{s.direction || "Update"}</span></h1>
        <div className="mt-6"><SignalCard s={s} contacts={s.contacts} hideLockedCta={s.locked} /></div>
        {s.locked && <JoinPremiumBox joinUrl={s.contacts.join_url} />}
        <p className="text-[11px] text-slate-500 mt-8">Research only. Not financial advice. Trading involves risk.</p>
      </main>
    </div>
  );
}

function LegacySignal({ src, sid }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    client.get(`/public/signal/${src}/${sid}`).then(({ data }) => setS(data)).catch(() => setErr(true));
  }, [src, sid]);

  if (err) return (
    <div className="min-h-screen grid place-items-center px-6 text-center" style={{ background: "var(--app-bg)" }} data-testid="signal-not-found">
      <div><div className="text-white font-head text-xl">Signal nahi mila</div>
        <Link to="/results" className="text-gold text-sm mt-3 inline-block">Live results dekhein →</Link></div>
    </div>
  );
  if (!s) return <div className="min-h-screen" style={{ background: "var(--app-bg)" }} />;
  const buy = s.direction === "BUY";

  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }} data-testid="signal-alert-page">
      <header className="max-w-2xl mx-auto px-5 pt-6 flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center bg-gold text-black font-head font-bold text-sm">F</div>
        <span className="font-head font-bold text-white">Fateh FX</span>
        <Link to="/results" data-testid="signal-results-link" className="ml-auto text-xs text-slate-400 hover:text-white transition-colors duration-200">Live Results</Link>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-gold" data-testid="signal-kind">{KIND[s.src]}</div>
        <h1 className="font-head font-bold text-white text-4xl sm:text-5xl mt-3 leading-tight" data-testid="signal-title">
          {s.instrument} <span className={buy ? "text-bull" : "text-bear"}>{s.direction}</span>
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><Clock size={12} />{when(s.created_at)}</span>
          <span data-testid="signal-status" className="px-2 py-0.5 border border-[color:var(--border-subtle)] text-slate-300">{s.status || "Live"}</span>
          {s.timeframe && <span>{s.timeframe} · {s.strategy}</span>}
          {s.outcome && <span className={`flex items-center gap-1 ${s.outcome === "win" ? "text-bull" : "text-bear"}`}><Trophy size={12} />{s.outcome === "win" ? "Profit" : "Closed"}{s.net_pips ? ` · ${s.net_pips} pips` : ""}</span>}
        </div>

        <div className="mt-8"><Levels s={s} /></div>

        {s.locked ? <JoinPremiumBox joinUrl={s.join_url} /> : (s.reasoning || s.invalidation) && (
          <div className="mt-6 surface px-5 py-4 text-sm text-slate-300 space-y-2" data-testid="signal-reasoning">
            {s.reasoning && <p className="flex gap-2"><Target size={15} className="text-gold mt-0.5 shrink-0" />{s.reasoning}</p>}
            {s.invalidation && <p className="flex gap-2 text-slate-400"><ShieldAlert size={15} className="text-bear mt-0.5 shrink-0" />{s.invalidation}</p>}
          </div>
        )}

        {!s.locked && <a href={s.join_url} data-testid="signal-join-premium-btn"
          className="mt-8 flex items-center justify-center gap-2 w-full sm:w-auto sm:inline-flex px-8 py-4 bg-gold text-black font-bold rounded-full hover:brightness-110 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
          Join Premium <ArrowRight size={18} />
        </a>}
        <p className="text-[11px] text-slate-500 mt-8">Research only. Not financial advice. Trading involves risk.</p>
      </main>
    </div>
  );
}
